import type { WebsiteEventColorsFragment } from '../../apps/admin/routes/WebsiteDesignerV2/WebsiteDesigner.graphql.generated';
import type { ColorSetFragment } from '@graphql/fragments/colorSet.graphql.generated';
import type { EventDesignFragment } from '@graphql/fragments/eventDesign.graphql.generated';
import type {
  EventDesignPreferenceBooleanFragment,
  EventDesignPreferenceColorPointerFragment,
  EventDesignPreferenceRangeFragment,
} from '@graphql/fragments/eventDesignPreferences.graphql.generated';
import type { ThemeFragment } from '@graphql/fragments/theme.graphql.generated';
import { DesignFeatureType, ColorPointerLocation, DesignLayoutType } from '@graphql/generated-base';
import { defaultTheme } from '@withjoy/joykit';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';

import chroma from 'chroma-js';

type HeaderLayoutWithPhotos<T> = T extends { photos?: unknown } ? T & { photos: NonNullable<T['photos']> } : never;

/**
 * Header presentation layouts with no photo slots at all, so nothing to check, replace or swap.
 *
 * `ReadonlySet` rather than `Set`: this is module state shared by the dashboard's default-photo CTA
 * and the publish wizard's photo check, and a stray `.add()` in either would silently change what
 * the other considers a photo layout.
 */
export const NON_PHOTO_LAYOUTS: ReadonlySet<string> = new Set(['monogram', 'noPhoto', 'videoBackground', 'videoBackgroundMonogram']);

export const isHeaderLayoutWithPhotos = <T extends SupportedHeaderPhotoLayoutSchema>(layout: T): layout is HeaderLayoutWithPhotos<T> => {
  return Boolean('photos' in layout && layout.photos);
};

export type EventDesignPreferences = Readonly<EventDesignFragment['designPreferences']>;
export type RangeDesignFeature = DesignFeatureType.brannanBackgroundTransparency;
export type BooleanDesignFeature = DesignFeatureType.brannanInnerFrameBorder;
export type ColorPointerDesignFeature = DesignFeatureType.alohaColorPreference | DesignFeatureType.brannanColorPreference;
export type GetDesignPreferenceReturn = EventDesignPreferenceColorPointerFragment | EventDesignPreferenceBooleanFragment | EventDesignPreferenceRangeFragment;

export function getDesignPreference(type: BooleanDesignFeature, designPreferences?: EventDesignPreferences): EventDesignPreferenceBooleanFragment | undefined;
export function getDesignPreference(type: RangeDesignFeature, designPreferences?: EventDesignPreferences): EventDesignPreferenceRangeFragment | undefined;
export function getDesignPreference(type: ColorPointerDesignFeature, designPreferences?: EventDesignPreferences): EventDesignPreferenceColorPointerFragment | undefined;
export function getDesignPreference<T extends DesignFeatureType>(type: T, designPreferences?: EventDesignPreferences): GetDesignPreferenceReturn | undefined {
  // Optional params + `?? []`/`?.` guards across this file degrade instead of throwing during hydration — see MCP_PREVIEW.md#partial-hydration.
  return (designPreferences ?? []).find(preference => preference.featureType === type);
}

type UnwrapReadonly<T> = T extends Readonly<infer U> ? U : T;

type ParseDesignPreferencesReturn = Readonly<{
  [DesignFeatureType.alohaColorPreference]: EventDesignPreferenceColorPointerFragment | undefined;
  [DesignFeatureType.alohaTextColorPreference]: EventDesignPreferenceColorPointerFragment | undefined;

  [DesignFeatureType.brannanColorPreference]: EventDesignPreferenceColorPointerFragment | undefined;
  [DesignFeatureType.brannanTextColorPreference]: EventDesignPreferenceColorPointerFragment | undefined;
  [DesignFeatureType.brannanBackgroundTransparency]: EventDesignPreferenceRangeFragment | undefined;
  [DesignFeatureType.brannanInnerFrameBorder]: EventDesignPreferenceBooleanFragment | undefined;
}>;

const BASE_PARSED_DESIGN_PREFERENCES: ParseDesignPreferencesReturn = (Object.keys(DesignFeatureType) as DesignFeatureType[]).reduce((acc, featureType: DesignFeatureType) => {
  acc[featureType] = undefined;
  return acc;
}, {} as UnwrapReadonly<ParseDesignPreferencesReturn>);

export const parseDesignPreferences = (designPreferences?: EventDesignPreferences): ParseDesignPreferencesReturn => {
  return (designPreferences ?? []).reduce(
    (acc, curr) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      acc[curr.featureType] = curr as any;
      return acc;
    },
    { ...BASE_PARSED_DESIGN_PREFERENCES },
  );
};

////////////////////////////////////////////////////////
// Color

export const isOfColorPointerType = <T extends ColorPointerLocation>(target: T, source?: ColorPointerLocation): source is T => {
  return target === source;
};

export const colorPointerUtils = {
  isThemePointer: (pointerLocation?: ColorPointerLocation): pointerLocation is ColorPointerLocation.theme => isOfColorPointerType(ColorPointerLocation.theme, pointerLocation),
  isEventPointer: (pointerLocation?: ColorPointerLocation): pointerLocation is ColorPointerLocation.event => isOfColorPointerType(ColorPointerLocation.event, pointerLocation),
} as const;

type ParseColorPaletteReturn = Readonly<{
  primaryFillColor: ColorSetFragment | undefined;
  primaryTextColor: ColorSetFragment | undefined;
  artBackgroundColor: ColorSetFragment | undefined;
  pageColor: ColorSetFragment | undefined;
  pageFontColor: ColorSetFragment | undefined;
}>;

const parseColorPalette = (colorPalette?: ReadonlyArray<ColorSetFragment>): ParseColorPaletteReturn => {
  const [primaryFillColor, primaryTextColor, artBackgroundColor, pageColor, pageFontColor] = colorPalette ?? [];
  return {
    primaryFillColor,
    primaryTextColor,
    artBackgroundColor,
    pageColor,
    pageFontColor,
  };
};

const COLOR_WHITE_HEX = '#FFFFFF';

const deriveColorPreferencesFromEventDesign = (
  eventDesign: { websiteLayout: { layoutType: DesignLayoutType }; designPreferences: EventDesignPreferences; colorPalette: ReadonlyArray<ColorSetFragment> },
  theme: {
    colorSchemes: Array<{ colors: Array<{ hex: string }> }>;
    websiteLayoutAlohaSettings?: ThemeFragment['websiteLayoutAlohaSettings'];
  },
) => {
  const { designPreferences, colorPalette, websiteLayout } = eventDesign;
  const isAlohaLayout = websiteLayout?.layoutType === DesignLayoutType.aloha;
  const isBrannanLayout = websiteLayout?.layoutType === DesignLayoutType.brannan;

  const { alohaColorPreference, brannanColorPreference } = parseDesignPreferences(designPreferences);

  const { primaryFillColor, primaryTextColor, artBackgroundColor: customArtBackgroundColor, pageColor, pageFontColor } = parseColorPalette(colorPalette);

  const useSuggestedThemeBackgroundColor = colorPointerUtils.isThemePointer(
    (isAlohaLayout ? alohaColorPreference?.colorPointerPosition : brannanColorPreference?.colorPointerPosition) ?? undefined,
  );

  const themeBackgroundColorHex = theme?.colorSchemes?.[0]?.colors?.[0]?.hex;
  const themeTextColorHex = theme?.colorSchemes?.[0]?.colors?.[1]?.hex;

  const defaultConsolidatedBackgroundColorHex = isBrannanLayout
    ? // If user chooses to not use the art background color for brannan,
      // we have historically defaulted to white
      COLOR_WHITE_HEX
    : // Aloha: default to the primary fill color (white if the palette hasn't hydrated yet on the preview path).
      (primaryFillColor?.color.hex ?? COLOR_WHITE_HEX);

  const consolidatedBackgroundColorHex = useSuggestedThemeBackgroundColor && themeBackgroundColorHex ? themeBackgroundColorHex : defaultConsolidatedBackgroundColorHex;

  return {
    useSuggestedThemeBackgroundColor,

    primaryFillColor,
    primaryTextColor,
    customArtBackgroundColor,
    pageColor,
    pageFontColor,

    /**
     * A derived value.
     *
     * Art background color that takes into consideration user choice of preferring theme or custom bacground color
     */
    consolidatedBackgroundColorHex,
    /**
     * A derived value.
     *
     * Art background color that takes into consideration user choice of preferring theme or custom bacground color
     *
     * Only available for users who have not experienced the new color treatment + and are using the Aloha layout.
     */
    consolidatedPrimaryTextColorHex:
      isAlohaLayout &&
      useSuggestedThemeBackgroundColor &&
      // If they've already experienced the new color treatment, we can't do this
      colorPalette?.length === 2
        ? themeTextColorHex
        : undefined,

    /**
     * A derived value.
     *
     * Art background color that takes into consideration user choice of preferring theme or custom bacground color
     */
    consolidatedBackgroundColorRgba: chroma(consolidatedBackgroundColorHex).rgba(),
  };
};

export const deriveWebsiteColorsFromEventDesign = (
  eventDesign: {
    websiteLayout: { layoutType: DesignLayoutType };
    designPreferences: EventDesignPreferences;
    colorPalette: ReadonlyArray<ColorSetFragment>;
  } & WebsiteEventColorsFragment,
  theme: {
    colorSchemes: Array<{ colors: Array<{ hex: string }> }>;
    websiteLayoutAlohaSettings?: ThemeFragment['websiteLayoutAlohaSettings'];
  },
) => {
  const { primaryFillColor, primaryTextColor, customArtBackgroundColor, pageColor, pageFontColor, consolidatedBackgroundColorHex, consolidatedPrimaryTextColorHex } =
    deriveColorPreferencesFromEventDesign(eventDesign, theme);

  return {
    // Previously colorPalette[0] -- existing
    accentColor:
      // New named color field
      eventDesign.linkAndButtonColor ||
      // Legacy color palette
      primaryFillColor?.color.hex ||
      defaultTheme.colors.black,

    // Previously colorPalette[1] -- existing
    artBackgroundFontColor:
      // New named color field
      eventDesign.artFontColor ||
      // Legacy color palette
      // consolidatedPrimaryTextColorHex is only available for legacy users that haven't experienced the new color treatment UI
      consolidatedPrimaryTextColorHex ||
      primaryTextColor?.color.hex ||
      defaultTheme.colors.black,

    // Previously colorPalette[2] -- new
    //
    // if customArtBackgroundColor is not set, use the consolidated background color
    // This scenario is possible when we enable the feature flag but the user is no longer editing their website design
    // and does not have a custom background color set.
    artBackgroundColor:
      // New named color field
      eventDesign.artBackgroundColor ||
      // Legacy color palette
      customArtBackgroundColor?.color?.hex ||
      consolidatedBackgroundColorHex ||
      defaultTheme.colors.white,

    // Previously colorPalette[3] -- new
    pageColor:
      // New named color field
      eventDesign.pageBackgroundColor ||
      // Legacy color palette
      pageColor?.color.hex ||
      defaultTheme.colors.white,

    // Previously colorPalette[4] -- new
    pageFontColor:
      // New named color field
      eventDesign.pageFontColor ||
      // Legacy color palette
      pageFontColor?.color.hex ||
      defaultTheme.colors.black,
  };
};
